import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, Injector, inject, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxDate, OnyxHttpErrorResponse } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDatePipe,
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxToastMessage,
  OnyxToastService,
  OnyxUploadComponent,
  onyxDateValidator,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import type { Driver } from '../../../../dashboard/drivers/common/interfaces/driver';
import { DriversService } from '../../../../dashboard/drivers/common/services/drivers.service';
import type { Employee } from '../../../../dashboard/management-panel/employees/common/interfaces/employee';
import { EmployeesService } from '../../../../dashboard/management-panel/employees/common/services/employees.service';
import { VALIDATION_ERROR_CODES } from '../../../constants/common/validation-error-codes';
import { DictionaryCode } from '../../../enums/dictionary-code';
import { ValidationHelper } from '../../../helpers/validation.helper';
import type { Unavailability } from '../../../interfaces/common/unavailability';
import { DictionariesService } from '../../../services/dictionaries.service';
import { UnavailabilityType } from '../unavailabilities.component';

export type UnavailabilityModalData = (
  | { type: UnavailabilityType.EMPLOYEE; item: Employee }
  | { type: UnavailabilityType.DRIVER; item: Driver }
) &
  (
    | { mode: OnyxFormMode.ADD }
    | { mode: OnyxFormMode.EDIT; unavailability: Unavailability }
  );

export type UnavailabilityForm = ReturnType<
  ReturnType<UnavailabilityModalComponent['buildForm']>['getRawValue']
>;

@Component({
  selector: 'app-unavailability-modal',
  imports: [
    OnyxModalComponent,
    OnyxDatepickerComponent,
    OnyxButtonComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxUploadComponent,
    AsyncPipe,
    OnyxNamePipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './unavailability-modal.component.html',
  styleUrl: './unavailability-modal.component.scss',
})
export class UnavailabilityModalComponent {
  protected readonly data = inject<UnavailabilityModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly employeesService = inject(EmployeesService);
  private readonly driversService = inject(DriversService);
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);
  private readonly onyxDatePipe = inject(OnyxDatePipe);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'unavailabilities';

  protected readonly OnyxFormMode = OnyxFormMode;
  protected readonly UnavailabilityType = UnavailabilityType;

  protected readonly unavailabilityReasons$ =
    this.dictionariesService.getDictionary(
      this.data.type === UnavailabilityType.EMPLOYEE
        ? DictionaryCode.EMPLOYEE_UNAVAILABILITY_REASON
        : DictionaryCode.DRIVER_UNAVAILABILITY_REASON,
    );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  constructor() {
    if (this.data.mode === OnyxFormMode.EDIT) {
      const { dateRange, reason, file } = this.data.unavailability;
      this.form.setValue({
        dateRange: dateRange,
        reason,
        file,
      });
    }
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();
    const action$ =
      this.data.mode === OnyxFormMode.ADD
        ? this.data.type === UnavailabilityType.EMPLOYEE
          ? this.employeesService.addEmployeeUnavailability(
              this.data.item.uuid,
              form,
            )
          : this.driversService.addDriverUnavailability(
              this.data.item.uuid,
              form,
            )
        : this.data.type === UnavailabilityType.EMPLOYEE
          ? this.employeesService.editEmployeeUnavailability(
              this.data.item.uuid,
              this.data.unavailability.uuid,
              form,
            )
          : this.driversService.editDriverUnavailability(
              this.data.item.uuid,
              this.data.unavailability.uuid,
              form,
            );

    this.loading.set(true);
    action$
      .subscribe({
        next: () => {
          this.toastService.showCustom(OnyxToastMessage.UPDATED_DATA);
          this.close$.next();
        },
        error: (response: OnyxHttpErrorResponse) => {
          if (VALIDATION_ERROR_CODES.includes(response.status)) {
            if (
              response.error.reason ===
              'The selected time overlaps with an existing unavailability period'
            ) {
              const { overlapingFrom, overlapingTo } = response.error;
              this.toastService.showError({
                message: this.translateService.instant(
                  `${this.I18N}.conflictingOverlap`,
                ),
                description: this.onyxDatePipe.transform(
                  overlapingFrom,
                  'date-dot',
                  { endDate: overlapingTo },
                ),
              });

              response.handled = true;
            }
          }

          throw response;
        },
      })
      .add(() => this.loading.set(false));
  }

  protected delete(): void {
    if (this.data.mode !== OnyxFormMode.EDIT) return;

    const action$ = {
      [UnavailabilityType.EMPLOYEE]:
        this.employeesService.deleteEmployeeUnavailability(
          this.data.item.uuid,
          this.data.unavailability.uuid,
        ),
      [UnavailabilityType.DRIVER]:
        this.driversService.deleteDriverUnavailability(
          this.data.item.uuid,
          this.data.unavailability.uuid,
        ),
    }[this.data.type];

    this.loading.set(true);
    action$
      .subscribe(() => {
        this.toastService.showCustom(OnyxToastMessage.UPDATED_DATA);
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      dateRange: this.fb.control<OnyxDate | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),
      reason: this.fb.control<string | null>(null, [Validators.required]),
      file: this.fb.control<File | null>(null),
    });
  }
}
